import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { getMonthLabel, MONTH_NUMBERS } from '@/constants/months'
import { createDDSMonthlyThemeSchema } from '@/schemas/dds-monthly-theme-schema'
import type {
	DDSMonthlyThemeInput,
	DDSMonthlyThemeOutput,
} from '@/types/dds-monthly-theme'

const THEME_TYPES = ['DIG', 'DMI'] as const

export interface DDSMonthlyThemeFormProps {
	mode: 'create' | 'edit'
	initial?: DDSMonthlyThemeOutput
	onSave: (data: DDSMonthlyThemeInput) => void
	onClose: () => void
}

export function DDSMonthlyThemeFormContent({
	mode,
	initial,
	onSave,
	onClose,
}: DDSMonthlyThemeFormProps) {
	const { t, i18n } = useTranslation()

	const [month, setMonth] = useState(initial ? String(initial.month) : '')
	const [type, setType] = useState<string>(initial?.type ?? '')
	const [description, setDescription] = useState(initial?.description ?? '')

	const handleSave = () => {
		const parsed = createDDSMonthlyThemeSchema.safeParse({
			month,
			type,
			description,
		})

		if (!parsed.success) {
			if (!month) {
				toast.error(t('administration.ddsMonthlyThemes.form.emptyMonthError'))
				return
			}
			if (!type) {
				toast.error(t('administration.ddsMonthlyThemes.form.emptyTypeError'))
				return
			}
			toast.error(
				t('administration.ddsMonthlyThemes.form.invalidDescriptionError'),
			)
			return
		}

		onSave(parsed.data)
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="monthly-theme-month">
					{t('administration.ddsMonthlyThemes.form.monthLabel')}
				</FieldLabel>
				<FieldContent>
					<Select value={month} onValueChange={setMonth}>
						<SelectTrigger id="monthly-theme-month" className="w-full">
							<SelectValue
								placeholder={t(
									'administration.ddsMonthlyThemes.form.monthPlaceholder',
								)}
							/>
						</SelectTrigger>
						<SelectContent>
							{MONTH_NUMBERS.map((value) => (
								<SelectItem key={value} value={String(value)}>
									{getMonthLabel(value, i18n.language)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</FieldContent>
			</Field>

			<Field>
				<FieldLabel htmlFor="monthly-theme-type">
					{t('administration.ddsMonthlyThemes.form.typeLabel')}
				</FieldLabel>
				<FieldContent>
					<Select value={type} onValueChange={setType}>
						<SelectTrigger id="monthly-theme-type" className="w-full">
							<SelectValue
								placeholder={t(
									'administration.ddsMonthlyThemes.form.typePlaceholder',
								)}
							/>
						</SelectTrigger>
						<SelectContent>
							{THEME_TYPES.map((value) => (
								<SelectItem key={value} value={value}>
									{value}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</FieldContent>
			</Field>

			<Field>
				<FieldLabel htmlFor="monthly-theme-description">
					{t('administration.ddsMonthlyThemes.form.descriptionLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="monthly-theme-description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder={t(
							'administration.ddsMonthlyThemes.form.descriptionPlaceholder',
						)}
						maxLength={255}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.ddsMonthlyThemes.form.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create'
						? t('administration.ddsMonthlyThemes.form.saveButton')
						: t('administration.ddsMonthlyThemes.form.saveEditButton')}
				</Button>
			</div>
		</div>
	)
}
